/**
 * Medidas corporales (Req. 17; tarea 19, 0.4.1): los tipos, sus rangos, cómo
 * se muestran y su cambio contra la primera y contra la anterior.
 *
 * Las longitudes se guardan en cm con lo que se escribió (Req. 5.4); la grasa
 * corporal, en %. Hay una medida de cada tipo por día: una nueva del mismo
 * tipo y día reemplaza a la anterior, con aviso. Nada se guarda calculado.
 */

import type { TipoMedida } from '@/db/types';
import { esFechaLocal, type FechaLocal } from './dates';
import { formatearNumero, leerDecimal } from './numbers';
import { aCm, desdeCm, esUnidadLongitud, type UnidadLongitud } from './units';

export const TIPOS_DE_MEDIDA: readonly TipoMedida[] = ['cintura', 'cadera', 'pecho', 'brazo', 'muslo', 'pantorrilla', 'cuello', 'grasa'];

export const NOMBRE_MEDIDA: Readonly<Record<TipoMedida, string>> = {
  cintura: 'Cintura',
  cadera: 'Cadera',
  pecho: 'Pecho',
  brazo: 'Brazo',
  muslo: 'Muslo',
  pantorrilla: 'Pantorrilla',
  cuello: 'Cuello',
  grasa: 'Grasa corporal',
};

/** Para los mensajes: "La cintura debe estar entre…". */
const CON_ARTICULO: Readonly<Record<TipoMedida, string>> = {
  cintura: 'La cintura',
  cadera: 'La cadera',
  pecho: 'El pecho',
  brazo: 'El brazo',
  muslo: 'El muslo',
  pantorrilla: 'La pantorrilla',
  cuello: 'El cuello',
  grasa: 'La grasa corporal',
};

export function esTipoMedida(valor: unknown): valor is TipoMedida {
  return typeof valor === 'string' && (TIPOS_DE_MEDIDA as readonly string[]).includes(valor);
}

/** Todas menos la grasa corporal, que va en %. */
export function esLongitud(tipo: TipoMedida): boolean {
  return tipo !== 'grasa';
}

/** Rangos válidos (Req. 17.6). */
export const MEDIDA_MIN_CM = 10;
export const MEDIDA_MAX_CM = 300;
export const GRASA_MIN = 2;
export const GRASA_MAX = 70;
export const NOTA_MEDIDA_MAX = 200;

const unDecimal = (v: number) => formatearNumero(v, { decimales: 1, fijos: false });

/** "10 y 300 cm", "4 y 118.1 in" o "2 y 70 %": los extremos que de verdad se aceptan, con un decimal. */
export function rangoDeMedida(tipo: TipoMedida, unidad: UnidadLongitud): string {
  if (!esLongitud(tipo)) return `${GRASA_MIN} y ${GRASA_MAX} %`;
  const min = Math.ceil(desdeCm(MEDIDA_MIN_CM, unidad) * 10) / 10;
  const max = Math.floor(desdeCm(MEDIDA_MAX_CM, unidad) * 10) / 10;
  return `${unDecimal(min)} y ${unDecimal(max)} ${unidad}`;
}

/** Lo que se guarda de una medida: el valor canónico (cm o %), o por qué no sirve. */
export type ValorDeMedida = { valido: true; canonico: number } | { valido: false; error: string };

/** Revisa un valor ya leído: positivo y en su rango (Req. 17.6), en la unidad en que se escribió. */
export function revisarValorDeMedida(valor: number, tipo: TipoMedida, unidad: UnidadLongitud): ValorDeMedida {
  if (!Number.isFinite(valor) || valor <= 0) return { valido: false, error: 'Escribe la medida con números, por ejemplo 80.5.' };
  if (!esLongitud(tipo)) {
    return valor >= GRASA_MIN && valor <= GRASA_MAX
      ? { valido: true, canonico: valor }
      : { valido: false, error: `${CON_ARTICULO[tipo]} debe estar entre ${rangoDeMedida(tipo, unidad)}.` };
  }
  if (!esUnidadLongitud(unidad)) return { valido: false, error: 'Elige cm o in.' };
  const cm = aCm(valor, unidad);
  return cm >= MEDIDA_MIN_CM && cm <= MEDIDA_MAX_CM
    ? { valido: true, canonico: cm }
    : { valido: false, error: `${CON_ARTICULO[tipo]} debe estar entre ${rangoDeMedida(tipo, unidad)}.` };
}

export type MedidaEscrita = { tipo: 'vacia' } | { tipo: 'valida'; valor: number; canonico: number } | { tipo: 'invalida'; error: string };

/** Un campo de la captura: vacío (no se guarda), válido o con su error. */
export function leerMedidaEscrita(texto: string, tipo: TipoMedida, unidad: UnidadLongitud): MedidaEscrita {
  if (texto.trim() === '') return { tipo: 'vacia' };
  const valor = leerDecimal(texto);
  if (valor === null) return { tipo: 'invalida', error: 'Escribe la medida con números, por ejemplo 80.5.' };
  const r = revisarValorDeMedida(valor, tipo, unidad);
  return r.valido ? { tipo: 'valida', valor, canonico: r.canonico } : { tipo: 'invalida', error: r.error };
}

/** Lo que estas cuentas leen de una medida. */
export interface MedidaBase {
  id: string;
  fecha: FechaLocal;
  tipo: TipoMedida;
  valor: number;
  capturado: { valor: number; unidad: string } | null;
  creadoEn?: string;
}

/**
 * ¿Se puede usar esta medida? Una que llegó en un respaldo editado a mano
 * puede traer la fecha, el tipo o el valor dañados (al restaurar solo se
 * avisa): no se muestra ni cuenta, y sigue guardada tal cual.
 */
export function medidaLegible(m: { fecha: unknown; tipo: unknown; valor: unknown }): boolean {
  return esFechaLocal(m.fecha) && esTipoMedida(m.tipo) && typeof m.valor === 'number' && Number.isFinite(m.valor) && m.valor > 0;
}

/** De la más antigua a la más reciente; el mismo día, la que se capturó primero. */
export function enOrden<T extends MedidaBase>(medidas: readonly T[]): T[] {
  return [...medidas].sort((a, b) => a.fecha.localeCompare(b.fecha) || (a.creadoEn ?? '').localeCompare(b.creadoEn ?? ''));
}

/**
 * Una por día: si un respaldo trajera dos del mismo tipo y día, cuenta la
 * última que se capturó (la app ya no deja dos).
 */
export function unaPorDia<T extends MedidaBase>(medidas: readonly T[]): T[] {
  const porDia = new Map<FechaLocal, T>();
  for (const m of enOrden(medidas)) porDia.set(m.fecha, m);
  return [...porDia.values()];
}

export interface ResumenDeMedida<T extends MedidaBase = MedidaBase> {
  cantidad: number;
  primera: T;
  ultima: T;
  /** La de antes de la última; null si solo hay una. */
  anterior: T | null;
  /** Última − primera, en cm o %; null si solo hay una (Req. 17.4). */
  cambioDesdePrimera: number | null;
  /** Última − anterior; null si solo hay una. */
  cambioDesdeAnterior: number | null;
}

/** El historial de un tipo (Req. 17.4); null sin medidas. */
export function resumenDeMedida<T extends MedidaBase>(medidas: readonly T[]): ResumenDeMedida<T> | null {
  const dias = unaPorDia(medidas);
  const primera = dias[0];
  const ultima = dias[dias.length - 1];
  if (primera === undefined || ultima === undefined) return null;
  const anterior = dias.length > 1 ? (dias[dias.length - 2] ?? null) : null;
  return {
    cantidad: dias.length,
    primera,
    ultima,
    anterior,
    cambioDesdePrimera: dias.length > 1 ? ultima.valor - primera.valor : null,
    cambioDesdeAnterior: anterior === null ? null : ultima.valor - anterior.valor,
  };
}

/**
 * La unidad que se propone para un tipo (design.md, 5.3): la de su última
 * medida; si no hay, la preferida. La grasa siempre va en %.
 */
export function unidadPropuestaDeMedida(medidas: readonly MedidaBase[], preferida: UnidadLongitud): UnidadLongitud {
  const ultima = enOrden(medidas).filter((m) => m.capturado !== null && esUnidadLongitud(m.capturado.unidad)).pop();
  const unidad = ultima?.capturado?.unidad;
  return esUnidadLongitud(unidad) ? unidad : preferida;
}

/** "80.0 cm", "31.5 in" o "24.5 %" (1 decimal, design.md 4.2). */
export function formatearMedida(canonico: number, tipo: TipoMedida, unidad: UnidadLongitud): string {
  if (!esLongitud(tipo)) return `${formatearNumero(canonico, { decimales: 1 })} %`;
  return `${formatearNumero(desdeCm(canonico, unidad), { decimales: 1 })} ${unidad}`;
}

/** "+0.5 cm", "−1.2 in", "0.0 cm" o "−0.8 %": un cambio con su signo. */
export function formatearCambioDeMedida(delta: number, tipo: TipoMedida, unidad: UnidadLongitud): string {
  const enUnidad = esLongitud(tipo) ? desdeCm(delta, unidad) : delta;
  const texto = formatearMedida(delta, tipo, unidad);
  return enUnidad > 0 && Math.round(enUnidad * 10) > 0 ? `+${texto}` : texto;
}

/** Los puntos de la gráfica de un tipo, ya en la unidad en que se muestran (Req. 17.4). */
export function puntosDeMedida(medidas: readonly MedidaBase[], tipo: TipoMedida, unidad: UnidadLongitud): { fecha: FechaLocal; valor: number }[] {
  return unaPorDia(medidas.filter((m) => m.tipo === tipo)).map((m) => ({
    fecha: m.fecha,
    valor: esLongitud(tipo) ? desdeCm(m.valor, unidad) : m.valor,
  }));
}
